import React from 'react';
import styled, { css } from 'styled-components';

import { Chip, Icon, formatNumber } from '@column/column-ui-kit';

import { CopyRow } from '~/app/pages/Transfers/_components/CopyRow';
import { TimelineStatus, TransferAmount } from '~/app/pages/Transfers/_components/Timeline/types';
import { Card, Flex, Inner, Text } from '~/styles';

interface TimelineEventDetailsProps {
  details?: { label: string; value: string | React.ReactNode; warning?: string }[];
  onClick?: () => void;
  icon?: React.ReactNode;
  title: string;
  secondaryTitle?: string;
  status?: TimelineStatus;
  subtitle?: string;
  amount?: TransferAmount;
  variant?: 'primary' | 'secondary';
  metadata?: string;
}

const Title = styled.span`
  display: flex;
  align-items: center;
  line-height: 16px;
  max-height: 16px;

  svg {
    opacity: 0;
    transform: translateX(-4px);
    transition:
      opacity 0.2s,
      transform 0.2s;
  }
`;

const Subtitle = styled(Text)<{ status?: TimelineStatus }>`
  color: ${({ theme }) => theme.secondary.blendToBackground(800)};
  display: flex;
  align-items: center;
  gap: 4px;

  svg {
    --icon-size: 16px;
  }

  span {
    color: ${({ theme }) => theme.secondary.blendToBackground(500)};
  }

  ${({ status }) =>
    status === 'pending' &&
    css`
      background: linear-gradient(
        to right,
        ${({ theme }) => theme.secondary.blendToBackground(100)} 20%,
        ${({ theme }) => theme.secondary.blendToBackground(400)} 40%,
        ${({ theme }) => theme.secondary.blendToBackground(400)} 60%,
        ${({ theme }) => theme.secondary.blendToBackground(100)} 80%
      );
      background-size: 200% auto;

      color: ${({ theme }) => theme.secondary.blendToBackground(800)};
      background-clip: text;
      text-fill-color: transparent;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;

      animation: pending 2s linear infinite;
      @keyframes pending {
        to {
          background-position: -200% center;
        }
      }
    `}

  ${({ status }) => status === 'success' && css``}
  ${({ status }) =>
    status === 'danger' &&
    css`
      color: ${({ theme }) => theme.danger.background};
      text-decoration: line-through;

      span {
        color: ${({ theme }) => theme.danger.blendToBackground(500)};
      }
    `}
`;

const Header = styled.div<{ onClick?: () => void }>`
  display: flex;
  align-items: start;
  padding: 8px;
  border-radius: 5px;
  transition:
    background 0.2s,
    box-shadow 0.2s;

  ${({ onClick }) =>
    onClick &&
    css`
      &:hover {
        cursor: pointer;
        background: ${({ theme }) => theme.secondary.blendToBackground(25)};
        box-shadow: 0 0 0 1px ${({ theme }) => theme.secondary.blendToBackground(150)};

        svg {
          opacity: 1;
          transform: translateX(0);
        }
      }

      &:active {
        ${Title} svg {
          transform: translateX(2px);
          transition: transform 0.1s;
        }
      }
    `}
`;

const StyledCard = styled(Card)`
  min-width: 248px;
  margin-left: 28px;
  margin-bottom: 16px;

  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    margin-left: 0;
  }
`;

const StyledChip = styled(Chip)`
  margin-top: -4px;
`;

export const TimelineEventDetails: React.FC<TimelineEventDetailsProps> = ({
  details,
  icon,
  onClick,
  title,
  secondaryTitle,
  status,
  subtitle,
  amount,
  variant,
  metadata,
}) => {
  return (
    <StyledCard variant={variant}>
      <Inner p={8}>
        <Header onClick={onClick}>
          <Flex gap={12} alignItems="center" auto>
            {icon}
            <Flex gap={4} flexDirection="column">
              <Title>
                <Flex gap={4} alignItems="center">
                  <Text as="h4" size={14} weight="medium">
                    {title}
                  </Text>
                  {secondaryTitle && (
                    <Text as="h4" color="light" size={14} weight="medium">
                      {secondaryTitle}
                    </Text>
                  )}
                  {onClick && <Icon.ChevronRight />}
                </Flex>
              </Title>
              {subtitle && (
                <Subtitle size={16} weight="medium" status={status} color="light">
                  {(() => {
                    const currencyCode =
                      amount?.partyType === 'receiver'
                        ? (amount?.settledCurrencyCode ?? 'USD')
                        : amount?.isIncoming
                          ? (amount?.instructedCurrencyCode ?? amount?.currencyCode ?? 'USD')
                          : (amount?.currencyCode ?? 'USD');
                    if (amount?.settledCurrencyCode || amount?.instructedCurrencyCode) {
                      const CurrencyIcon =
                        currencyCode && Icon.Currency[currencyCode as keyof typeof Icon.Currency]
                          ? Icon.Currency[currencyCode as keyof typeof Icon.Currency]
                          : Icon.Currency.USD;
                      return <CurrencyIcon />;
                    }
                    return null;
                  })()}{' '}
                  {(() => {
                    const amountValue =
                      amount?.partyType === 'receiver'
                        ? amount?.settledAmount || amount?.value
                        : amount?.isIncoming
                          ? amount?.instructedAmount || amount?.value
                          : amount?.value;
                    return `${amount?.isAdding === false ? '-' : ''}${formatNumber(amountValue)}`;
                  })()}{' '}
                  <span>
                    {amount?.partyType === 'receiver'
                      ? (amount?.settledCurrencyCode ?? 'USD')
                      : amount?.isIncoming
                        ? (amount?.instructedCurrencyCode ?? amount?.currencyCode ?? 'USD')
                        : (amount?.currencyCode ?? 'USD')}
                  </span>
                </Subtitle>
              )}
            </Flex>
          </Flex>
          {metadata && <StyledChip size="large">{metadata}</StyledChip>}
        </Header>
      </Inner>
      {details &&
        details.map(({ label, value, warning }) => (
          <CopyRow
            key={label}
            label={label}
            value={value}
            warning={warning}
            isMonospaced={
              label === 'Bank Account ID' ||
              label === 'Counterparty ID' ||
              label === 'Loan ID' ||
              label === 'Account Number ID'
            }
          />
        ))}
    </StyledCard>
  );
};
